<template>
  <div class="watch-page">
    <!-- 页面标题区 -->
    <header class="page-header">
      <div class="header-left">
        <div class="title-indicator"></div>
        <h2 class="page-title">观看记录</h2>
      </div>
      <div class="header-right">
        <span class="record-count">共 {{ totalRecords }} 条记录</span>
      </div>
    </header>

    <!-- 观看记录表格 -->
    <section class="table-section">
      <!-- Loading 遮罩 -->
      <div v-show="loading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
      </div>

      <div v-show="!loading" class="table-wrapper">
        <table class="watch-table">
          <thead>
            <tr>
              <th>视频封面</th>
              <th>视频标题</th>
              <th>观看进度</th>
              <th>观看时长</th>
              <th>最后观看时间</th>
              <th class="text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(record, index) in watchRecords" :key="index">
              <!-- 视频封面 -->
              <td>
                <div class="video-thumbnail" @click="handleContinueWatch(record)">
                  <img :src="record.thumbnail" :alt="record.isTrainingMaterial ? '培训资料封面' : '视频封面'" />
                  <div class="play-overlay">
                    <i :class="record.isTrainingMaterial ? 'el-icon-reading' : 'el-icon-video-play'"></i>
                  </div>
                </div>
              </td>

              <!-- 视频标题 -->
              <td>
                <a href="#" class="video-title" @click.prevent="handleContinueWatch(record)">{{ record.title }}</a>
              </td>

              <!-- 观看进度 -->
              <td>
                <div class="progress-wrapper">
                  <div class="progress-label">
                    <span>{{ record.progress }}%</span>
                  </div>
                  <div class="progress-bar">
                    <div
                      class="progress-fill"
                      :class="{ 'completed': record.progress === 100 }"
                      :style="{ width: record.progress + '%' }"
                    ></div>
                  </div>
                </div>
              </td>

              <!-- 观看时长 -->
              <td>
                <span class="duration">{{ record.duration }} 分钟</span>
              </td>

              <!-- 最后观看时间 -->
              <td>
                <span class="watch-time">{{ record.lastWatchTime }}</span>
              </td>

              <!-- 操作 -->
              <td class="text-right">
                <button
                  class="action-btn"
                  :class="record.progress === 100 ? 'replay' : 'continue'"
                  @click="handleContinueWatch(record)"
                >
                  <i :class="getActionIcon(record)"></i>
                  {{ getActionText(record) }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页器 -->
      <div class="pagination">
        <div class="pagination-info">
          显示 {{ (currentPage - 1) * pageSize + 1 }} 到 {{ Math.min(currentPage * pageSize, totalRecords) }} 条，共 {{ totalRecords }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            <i class="el-icon-arrow-left"></i>
          </button>

          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-number"
            :class="{ active: page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <span v-if="totalPages > 5" class="page-ellipsis">...</span>

          <button
            v-if="totalPages > 5"
            class="page-number"
            @click="goToPage(totalPages)"
          >
            {{ totalPages }}
          </button>

          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getWatchRecordPage, getUserInfo, recordApeTrainingMaterialView } from '@/api/api'
import trainingMaterialCover from '@/assets/image/training-material-cover.svg'

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm3u8', 'avi', 'wmv', 'flv', 'mkv', 'webm']

export default {
  name: 'Watch',
  data() {
    return {
      // 观看记录数据
      watchRecords: [],
      // 分页相关
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      // 用户信息
      userInfo: null,
      // 当前用户完整数据
      currentUser: null,
      // 加载状态
      loading: false,
      // 培训资料固定封面
      trainingMaterialCover
    }
  },
  computed: {
    // 总页数
    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize)
    },
    // 可见页码
    visiblePages() {
      const pages = []
      const maxVisible = 3

      for (let i = 1; i <= Math.min(maxVisible, this.totalPages); i++) {
        pages.push(i)
      }

      return pages
    }
  },
  async created() {
    await this.loadUserInfo()
    await this.loadWatchRecords()
  },
  methods: {
    // 加载用户信息
    async loadUserInfo() {
      try {
        const res = await getUserInfo()
        if (res.success === true || res.code === 1000) {
          this.currentUser = res.data
          this.userInfo = res.data
        } else {
          this.$message.error(res.message || res.msg || '获取用户信息失败')
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.$message.error('网络连接出错，请刷新重试')
      }
    },
    // 加载观看记录
    async loadWatchRecords() {
      if (!this.currentUser || !this.currentUser.id) {
        return
      }

      try {
        this.loading = true
        const params = {
          userId: this.currentUser.id,
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }

        const res = await getWatchRecordPage(params)

        if (res.code === 1000) {
          const pageData = res.data || {}
          const records = Array.isArray(pageData.records) ? pageData.records : []
          this.watchRecords = records.map(record => this.normalizeWatchRecord(record))
          this.totalRecords = pageData.total || 0
        } else {
          this.$message.error(res.message || res.msg || '获取观看记录失败')
        }
      } catch (error) {
        console.error('获取观看记录异常:', error)
        this.$message.error('网络连接出错，请刷新重试')
      } finally {
        this.loading = false
      }
    },
    // 上一页
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadWatchRecords()
      }
    },
    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadWatchRecords()
      }
    },
    // 跳转到指定页
    goToPage(page) {
      this.currentPage = page
      this.loadWatchRecords()
    },
    getFileUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      return this.$store.state.HOST + path
    },
    getFileExt(path) {
      if (!path) return ''
      const cleanPath = String(path).split('?')[0]
      const dotIndex = cleanPath.lastIndexOf('.')
      return dotIndex >= 0 ? cleanPath.slice(dotIndex + 1).toLowerCase() : ''
    },
    isTrainingMaterialRecord(record) {
      if (!record) return false
      return String(record.isTrainingMaterial || '') === '1' || record.categoryId === 'training_material_sync'
    },
    isVideoMaterialRecord(record) {
      const ext = String(
        record.materialFileType ||
        record.videoFormat ||
        this.getFileExt(record.materialFileUrl || record.videoUrl || '')
      ).toLowerCase()
      return VIDEO_EXTENSIONS.includes(ext)
    },
    normalizeProgress(progress) {
      const value = Number(progress)
      if (!Number.isFinite(value)) {
        return 0
      }
      return Math.max(0, Math.min(100, Math.round(value)))
    },
    normalizeWatchRecord(record) {
      const normalized = { ...record }
      const isTrainingMaterial = this.isTrainingMaterialRecord(record)
      const thumbnail = isTrainingMaterial
        ? this.trainingMaterialCover
        : (this.getFileUrl(record.thumbnail) || this.trainingMaterialCover)
      return {
        ...normalized,
        videoId: record.videoId ? String(record.videoId) : '',
        materialId: record.materialId ? String(record.materialId) : '',
        isTrainingMaterial,
        materialFileUrl: record.materialFileUrl || record.videoUrl || '',
        materialFileType: String(record.materialFileType || record.videoFormat || '').toLowerCase(),
        thumbnail,
        progress: this.normalizeProgress(record.progress),
        duration: Number(record.duration) || 0
      }
    },
    getActionText(record) {
      if (record && record.isTrainingMaterial) {
        return record.progress === 100 ? '再次查看' : '继续查看'
      }
      return record.progress === 100 ? '再次观看' : '继续观看'
    },
    getActionIcon(record) {
      if (record && record.progress === 100) {
        return 'el-icon-refresh'
      }
      if (record && record.isTrainingMaterial) {
        return 'el-icon-reading'
      }
      return 'el-icon-video-play'
    },
    async recordTrainingMaterialView(materialId) {
      if (!materialId) {
        return
      }
      try {
        await recordApeTrainingMaterialView({ id: materialId })
      } catch (error) {
        console.error('记录培训资料查阅次数失败:', error)
      }
    },
    handleTrainingMaterialJump(record) {
      const fileUrl = record.materialFileUrl || record.videoUrl || ''
      if (!fileUrl) {
        this.$message.warning('培训资料文件不存在')
        return
      }
      if (this.isVideoMaterialRecord(record)) {
        this.$router.push({
          path: '/videoPlay',
          query: {
            id: record.videoId || '',
            from: 'training',
            fileUrl,
            title: record.title || '培训资料',
            materialId: record.materialId || ''
          }
        })
        return
      }
      const targetUrl = this.getFileUrl(fileUrl)
      if (!targetUrl) {
        this.$message.warning('培训资料文件不存在')
        return
      }
      window.open(targetUrl, '_blank')
      this.recordTrainingMaterialView(record.materialId)
    },
    // 继续观看/再次观看
    handleContinueWatch(record) {
      if (!record) {
        this.$message.error('记录不存在')
        return
      }
      if (record.isTrainingMaterial) {
        this.handleTrainingMaterialJump(record)
        return
      }
      if (!record.videoId) {
        this.$message.error('视频ID不存在')
        return
      }
      // 根据 visibility 字段传递 from 参数
      // visibility: "0" -> public, "1" -> department, "3" -> activity
      let fromParam = 'activity'
      const visibility = String(record.visibility || '')
      if (visibility === '0') {
        fromParam = 'public'
      } else if (visibility === '1') {
        fromParam = 'department'
      }

      this.$router.push({
        path: '/videoPlay',
        query: { id: record.videoId, from: fromParam }
      })
    }
  }
}
</script>

<style scoped>
.watch-page {
  width: 100%;
}

/* 页面标题区 */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-indicator {
  width: 6px;
  height: 24px;
  background: #1a71ff;
  border-radius: 999px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.record-count {
  font-size: 14px;
  color: #64748b;
}

/* 表格区域 */
.table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  min-height: 400px;
}

/* Loading 遮罩 */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #1a71ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.table-wrapper {
  overflow-x: auto;
}

.watch-table {
  width: 100%;
  border-collapse: collapse;
}

.watch-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.watch-table th {
  padding: 16px 24px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.watch-table th.text-right {
  text-align: right;
}

.watch-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.watch-table tbody tr:hover {
  background: #f8fafc;
}

.watch-table td {
  padding: 16px 24px;
}

.watch-table td.text-right {
  text-align: right;
}

/* 视频封面 */
.video-thumbnail {
  width: 96px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  position: relative;
  cursor: pointer;
}

.video-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.video-thumbnail:hover .play-overlay {
  opacity: 1;
}


/* 视频标题 */
.video-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 320px;
  transition: color 0.2s;
}

.video-title:hover {
  color: #1a71ff;
}

/* 观看进度 */
.progress-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 120px;
}

.progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label span {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #1a71ff;
  border-radius: 999px;
  transition: width 0.3s;
}

.progress-fill.completed {
  background: #52c41a;
}

/* 观看时长 */
.duration {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

/* 最后观看时间 */
.watch-time {
  font-size: 14px;
  color: #64748b;
}

/* 操作按钮 */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 8px;
  border: none;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.continue {
  background: #1a71ff;
  color: white;
}

.action-btn.continue:hover {
  background: #1557d0;
}

.action-btn.replay {
  background: #f1f5f9;
  color: #64748b;
}

.action-btn.replay:hover {
  background: #e2e8f0;
}


/* 分页器 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  background: #f8fafc;
}


.page-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s;
}

.page-number:hover {
  background: #f8fafc;
}

.page-number.active {
  background: #1a71ff;
  color: white;
  border-color: #1a71ff;
  box-shadow: 0 1px 3px rgba(26, 113, 255, 0.3);
}

.page-ellipsis {
  color: #cbd5e1;
  padding: 0 4px;
}

/* 响应式 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .page-title {
    font-size: 18px;
  }

  /* 表格卡片化 */
  .table-wrapper {
    overflow-x: visible;
    padding: 12px;
  }

  /* 隐藏表头 */
  .watch-table thead {
    display: none;
  }

  .watch-table {
    display: block;
    min-width: auto;
  }

  /* 将tr转换为独立卡片 */
  .watch-table tbody tr {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    margin-bottom: 12px;
    background: white;
    align-items: stretch;
  }

  .watch-table tbody tr:hover {
    background: white;
    border-color: #cbd5e1;
  }

  /* 重置td基础样式 */
  .watch-table tbody td {
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
  }

  /* 第1列：视频封面 - 左侧固定宽度 */
  .watch-table tbody td:nth-child(1) {
    width: 35%;
    order: 1;
    padding: 0;
    margin: 0;
    align-items: center;
    flex-shrink: 0;
  }

  .watch-table tbody td:nth-child(1) .video-thumbnail {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  /* 第2列：视频标题 - 右侧自动填充 */
  .watch-table tbody td:nth-child(2) {
    flex: 1;
    order: 2;
    padding: 0;
    margin: 0;
    min-width: 0;
    align-items: center;
  }

  .watch-table tbody td:nth-child(2) .video-title {
    max-width: 100%;
    font-size: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* 第3列：观看进度 - 独占一行 */
  .watch-table tbody td:nth-child(3) {
    width: 100%;
    order: 3;
    padding: 0;
    margin: 0;
    align-items: center;
  }

  .progress-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: auto;
    flex-direction: row;
  }

  .progress-label {
    min-width: 40px;
    display: flex;
    justify-content: flex-start;
  }

  .progress-label span {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    white-space: nowrap;
  }

  .progress-bar {
    flex: 1;
    height: 8px;
    background: #f1f5f9;
    border-radius: 999px;
    overflow: hidden;
  }

  /* 第4列和第5列：时长与时间 - 并排显示 */
  .watch-table tbody td:nth-child(4),
  .watch-table tbody td:nth-child(5) {
    flex: 1;
    order: 4;
    padding: 0;
    margin: 0;
    align-items: center;
  }

  .duration,
  .watch-time {
    font-size: 12px;
    color: #64748b;
  }

  /* 第6列：操作按钮 - 全宽底部按钮 */
  .watch-table tbody td:nth-child(6) {
    width: 100%;
    order: 5;
    padding: 0;
    margin: 0;
    align-items: center;
    justify-content: center;
  }

  .watch-table tbody td:nth-child(6) .action-btn {
    width: 100%;
    padding: 10px 16px;
    font-size: 13px;
    border-radius: 6px;
  }

  /* 分页器优化 */
  .pagination {
    flex-direction: column;
    gap: 12px;
    padding: 12px 24px;
  }

  .pagination-info {
    font-size: 13px;
    text-align: center;
    width: 100%;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
  }

  /* 隐藏中间的页码按钮和省略号 */
  .page-number {
    display: none;
  }

  .page-ellipsis {
    display: none;
  }

  /* 放大箭头按钮 */
  .page-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .page-btn:not(:disabled):hover {
    background: #f1f5f9;
  }
}
</style>
